import type { StudentAssignment, WorkStatus } from '@pebblecode/shared';
import { useState } from 'react';

import { api } from '../api/client.js';
import { onLinkClick } from '../app/router.js';
import { Icon } from '../components/Icon.js';
import { useT } from '../i18n/index.js';
import { useDueText } from './dueText.js';

export function StatusTag({ status, late }: { status: WorkStatus; late: boolean }) {
  const t = useT();
  const tone =
    status === 'solved' || status === 'submitted'
      ? 'tag-ok'
      : status === 'started'
        ? 'tag-step'
        : '';
  return (
    <span className="status-tags">
      <span className={`tag ${tone}`}>{t.homework.status[status]}</span>
      {late && <span className="tag tag-error">{t.homework.late}</span>}
    </span>
  );
}

/** A student's homework: open it, hand it in, read the teacher's feedback. */
export function HomeworkList({
  items,
  showClass = false,
  onChange,
}: {
  items: StudentAssignment[];
  showClass?: boolean;
  onChange: () => void;
}) {
  const t = useT();
  const dueText = useDueText();
  const [busy, setBusy] = useState<string | null>(null);
  if (items.length === 0) return <p className="form-note">{t.homework.none}</p>;

  const submit = async (id: string, submitted: boolean) => {
    setBusy(id);
    try {
      await api.submitAssignment(id, submitted);
      onChange();
    } finally {
      setBusy(null);
    }
  };

  return (
    <ul className="hw-list">
      {items.map((a) => (
        <li key={a.id} className="hw-item">
          <div className="hw-item-top">
            <strong>{a.title}</strong>
            <StatusTag status={a.status} late={a.late} />
          </div>
          <span className="hw-meta">
            {showClass && `${a.className} · `}
            {dueText(a.dueDate)}
          </span>
          {a.note && <p className="hw-note">{a.note}</p>}
          {a.kind === 'project' && a.status !== 'submitted' && (
            <p className="form-note">{t.homework.projectNote}</p>
          )}
          {a.feedback && (
            <p className="hw-feedback">
              <strong>{t.homework.feedback}</strong>
              {a.feedback}
            </p>
          )}
          <div className="button-row">
            <a href={`/homework/${a.id}`} onClick={onLinkClick} className="btn btn-sm btn-primary">
              <Icon name="play" size={12} />
              {t.homework.open}
            </a>
            {a.status === 'started' && (
              <button
                className="btn btn-sm"
                disabled={busy === a.id}
                onClick={() => void submit(a.id, true)}
              >
                {t.homework.handIn}
              </button>
            )}
            {a.status === 'submitted' && (
              <button
                className="btn btn-sm"
                disabled={busy === a.id}
                onClick={() => void submit(a.id, false)}
              >
                {t.homework.takeBack}
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
